import { FC, useEffect } from 'react'
import {
  Controller,
  useFieldArray,
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import { localizedStrings } from '@core/strings'
import { useToast } from '@core/toast'
import { useUpdateProjectMapConfiguration } from '@core/react-query/features/projects/maps'
import { Project } from '@core/entities/project'
import { ProjectMap } from '@core/entities/project/ProjectMap'
import { Box, Flex, Text } from '@fundamentals'
import { TextField, Button, IconButton } from '@common/material'
import { Add, DeleteOutline } from '@mui/icons-material'

interface DisclaimerFormData {
  disclaimer: string
  disclaimerActions: { url: string; label: string }[]
}

const MAX_DISCLAIMER_CHARACTERS = 200

type DisclaimerTabPanelProps = {
  project: Project
  active: boolean
  projectMapConfiguration: ProjectMap
}

export const DisclaimerTabPanel: FC<DisclaimerTabPanelProps> = ({
  project,
  active,
  projectMapConfiguration,
}) => {
  const canEditProjectMap = project?.canEdit()
  const toast = useToast()
  const { control, handleSubmit, reset } = useForm<DisclaimerFormData>({
    defaultValues: {
      disclaimer: '',
      disclaimerActions: [],
    },
  })

  const {
    fields: disclaimerActionFields,
    append: appendDisclaimerAction,
    remove: removeDisclaimerAction,
  } = useFieldArray({
    control,
    name: 'disclaimerActions',
  })

  const updateProjectMapConfiguration = useUpdateProjectMapConfiguration({
    onSuccess: (response) => {
      reset({
        disclaimer: response.disclaimer,
        disclaimerActions: response.disclaimerActions?.length
          ? response.disclaimerActions
          : [],
      })

      toast.success(localizedStrings.updated)
    },
  })

  useEffect(() => {
    if (!projectMapConfiguration) return

    reset({
      disclaimer: projectMapConfiguration.disclaimer,
      disclaimerActions: projectMapConfiguration.disclaimerActions?.length
        ? projectMapConfiguration.disclaimerActions
        : [],
    })
  }, [projectMapConfiguration, reset])

  const onSubmit: SubmitHandler<DisclaimerFormData> = (formData) => {
    updateProjectMapConfiguration.mutate({
      projectId: project.id,
      disclaimer: formData.disclaimer,
      mapBoxStyleUrl: projectMapConfiguration?.mapBoxStyleUrl || '',
      mapCenterLongitude: projectMapConfiguration?.mapCenterLongitude || 0,
      mapCenterLatitude: projectMapConfiguration?.mapCenterLatitude || 0,
      mapZoom: projectMapConfiguration?.mapZoom || 0,
      canZoom: projectMapConfiguration?.canZoom || true,
      maxBounds: projectMapConfiguration?.maxBounds || null,
      ...(formData.disclaimerActions && {
        disclaimerActions: formData.disclaimerActions,
      }),
    })
  }

  return (
    <Box hidden={!active}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex flexDirection='column' p={2}>
          <Controller
            name='disclaimer'
            control={control}
            render={({ field: { onChange, ...field }, fieldState }) => {
              return (
                <TextField
                  label={localizedStrings.disclaimer}
                  placeholder={localizedStrings.disclaimer}
                  rows={3}
                  sx={{ mb: 2 }}
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    `${
                      field.value?.length || 0
                    }/${MAX_DISCLAIMER_CHARACTERS} characters`
                  }
                  onChange={(e) =>
                    e.target.value.length <= MAX_DISCLAIMER_CHARACTERS &&
                    onChange(e)
                  }
                  slotProps={{
                    formHelperText: {
                      error: !!fieldState.error,
                      sx: { pt: 0.5, pb: 2 },
                    },
                  }}
                  fullWidth
                  multiline
                  {...field}
                />
              )
            }}
          />

          <Box sx={{ mb: 2 }}>
            <Flex
              justifyContent='space-between'
              alignItems='flex-end'
              sx={{ mb: 2 }}
            >
              <Text>{localizedStrings.disclaimerActions}</Text>
              <Button
                variant='outlined'
                color='primary'
                onClick={() => appendDisclaimerAction({ url: '', label: '' })}
                size='small'
              >
                <Add />
                <Text>{localizedStrings.addLink}</Text>
              </Button>
            </Flex>
            {disclaimerActionFields.map((field, actionIndex) => (
              <Box
                key={field.id}
                mt={actionIndex > 0 ? 2 : 0}
                sx={{
                  backgroundColor: 'grey.100',
                  borderRadius: 1,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'grey.300',
                }}
              >
                <Flex
                  justifyContent='space-between'
                  alignItems='center'
                  sx={{ mb: 2 }}
                >
                  <Text variant='body2'>
                    {localizedStrings.link} {actionIndex + 1}
                  </Text>
                  <IconButton
                    color='error'
                    onClick={() => removeDisclaimerAction(actionIndex)}
                  >
                    <DeleteOutline />
                  </IconButton>
                </Flex>
                <Controller
                  name={`disclaimerActions.${actionIndex}.url`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={localizedStrings.url}
                      fullWidth
                      sx={{ mb: 2, backgroundColor: 'white' }}
                      slotProps={{
                        inputLabel: {
                          shrink: !!field.value,
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name={`disclaimerActions.${actionIndex}.label`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={localizedStrings.label}
                      fullWidth
                      sx={{ backgroundColor: 'white' }}
                      slotProps={{
                        inputLabel: {
                          shrink: !!field.value,
                        },
                      }}
                    />
                  )}
                />
              </Box>
            ))}
          </Box>

          <Button
            variant='contained'
            color='primary'
            type='submit'
            disabled={!canEditProjectMap}
          >
            {localizedStrings.submit}
          </Button>
        </Flex>
      </form>
    </Box>
  )
}
